/**
 * 图片存储服务
 * 
 * 核心目标：提供高性能的本地图片管理能力，替代传统后端存储。
 * 技术方案：IndexedDB + Content Addressing (SHA-256)
 * 
 * 特性：
 * 1. 自动去重：相同内容的图片只会存储一份，节省空间。
 * 2. 零阻塞：全异步操作，不影响 UI 线程。
 * 3. 强类型：完整的 TypeScript 类型支持。
 */

export interface ImageRecord {
  id: string;       // 图片内容的 SHA-256 哈希值，作为唯一索引
  blob: Blob;       // 图片原始二进制数据
  mimeType: string; // MIME 类型 (image/png, etc.)
  width: number;    // 图片原始宽度
  height: number;   // 图片原始高度
  createdAt: number;// 创建时间戳
}

class ImageStorageService {
  private dbName = 'tylas_assets_db';
  private storeName = 'images';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * 初始化数据库连接
   * 使用单例 Promise 防止并发调用导致的多次连接
   */
  public async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('ImageStorage: Database open failed', event);
        reject(event);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        // 创建对象仓库，使用 'id' (hash) 作为主键
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };
    });

    return this.initPromise;
  }

  /**
   * 保存图片到本地数据库
   * @param blob 图片文件对象
   * @returns 返回图片的唯一哈希 ID (SHA-256)
   */
  public async saveImage(blob: Blob): Promise<string> {
    await this.init();
    
    // 1. 计算哈希 (Content Addressing 的核心)
    const hash = await this.computeHash(blob);

    // 2. 检查是否已存在 (去重)
    const existing = await this.getImage(hash);
    if (existing) {
      console.log(`ImageStorage: Image ${hash} already exists, skipping save.`);
      return hash;
    }

    // 3. 获取图片尺寸
    // 使用 createImageBitmap 是最高效的方案，不仅不需要 DOM，还能在 Worker 中运行
    const bitmap = await createImageBitmap(blob);
    const { width, height } = bitmap;
    bitmap.close(); // 及时释放显存资源

    // 4. 构造记录
    const record: ImageRecord = {
      id: hash,
      blob: blob,
      mimeType: blob.type,
      width,
      height,
      createdAt: Date.now(),
    };

    // 5. 写入数据库
    return new Promise((resolve, reject) => {
      if (!this.db) return reject('Database not initialized');
      
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.add(record);

      request.onsuccess = () => resolve(hash);
      request.onerror = (e) => reject(e);
    });
  }

  /**
   * 通过 ID 获取图片记录
   * @param id 图片的哈希 ID
   */
  public async getImage(id: string): Promise<ImageRecord | undefined> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) return reject('Database not initialized');

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result as ImageRecord | undefined);
      };
      request.onerror = (e) => reject(e);
    });
  }

  /**
   * 计算 Blob 的 SHA-256 哈希值
   * 转为十六进制字符串返回
   */
  private async computeHash(blob: Blob): Promise<string> {
    const buffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    // 转换为 hex 字符串
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
}

export const imageStorage = new ImageStorageService();
